import ActionButton from "@/components/buttons/ActionButton";
import { selectStandardProps } from "@/utils/componentUtils";
import { App as AntApp, Card, Flex, Form, Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { t } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetPermissionDefinitions } from "../../../../common/api/queries";
import { rowGutter } from "../../../../common/constants";
import { Feature, Permission, PermissionFinancialSector } from "../../../../common/security/enums";
import { useCurrentAgentAccount } from "../../../auth/hooks";
import { PermissionDefinitions } from "../../../enumerations/types";
import { PermissionGroup } from "../../enums";
import { AdminCreateUpdateAgentUserAccount } from "../../types";

const getSectorOptions = (definitions: PermissionDefinitions | null | undefined, allowedFeatures: Feature[]) =>
  (definitions?.financialSectors ?? [])
    .filter(({ feature }) => allowedFeatures.includes(feature))
    .map(({ financialSector }) => ({
      value: financialSector,
      label:
        financialSector === PermissionFinancialSector.OTHERS
          ? t("product.enums.financialSector." + financialSector)
          : t("agent.enums.financialSector." + financialSector),
    }));

interface Props {
  allowedFeatures: Feature[];
  onPermissionsSet: (permissions: Permission[]) => void;
}

export const DefaultPermissionsFormPart = ({ allowedFeatures, onPermissionsSet }: Props) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const topAgentType = useCurrentAgentAccount()?.agent.topAgentType;

  const { data: permissionDefinitions } = useGetPermissionDefinitions();

  const sectorOptions = getSectorOptions(permissionDefinitions, allowedFeatures);
  const permissionGroupOptions = (permissionDefinitions?.permissionGroups ?? [])
    .filter(({ topAgentTypes }) => !!topAgentType && topAgentTypes.includes(topAgentType))
    .map(({ permissionGroup }) => ({
      value: permissionGroup,
      label: t("user.enums.permissionsGroup." + permissionGroup),
    }));

  const [sectors, setSectors] = useState<PermissionFinancialSector[]>();
  const selectedSectors = sectors ?? sectorOptions.map((option) => option.value);

  const form = Form.useFormInstance<AdminCreateUpdateAgentUserAccount>();
  const permissionGroup = Form.useWatch("permissionGroup", form);

  const handlePermissionsSetClick = () => {
    if (!permissionDefinitions || !permissionGroup || selectedSectors.length === 0) {
      message.error(t("user.helpers.defaultPermissionsFillError"));
      return;
    }

    const permissionsToRemove = [
      ...permissionDefinitions.financialSectors
        .filter(({ financialSector }) => !selectedSectors.includes(financialSector))
        .flatMap(({ permissions }) => permissions),
      ...permissionDefinitions.features
        .filter(({ feature }) => !allowedFeatures.includes(feature))
        .flatMap(({ permissions }) => permissions),
    ];

    onPermissionsSet(
      permissionDefinitions.permissionGroups
        .find((group) => group.permissionGroup === permissionGroup)
        ?.defaultPermissions.filter((permission) => !permissionsToRemove.includes(permission)) ?? [],
    );
  };

  return (
    <Card size="small" className="margin-bottom-medium">
      <Flex gap={rowGutter} align="end" justify="end">
        <div style={{ flex: 1 }}>
          <p className="margin-bottom-tiny">{t("common.financialSectors")}</p>
          <Select<PermissionFinancialSector[], DefaultOptionType>
            style={{ width: "100%" }}
            {...selectStandardProps}
            allowClear
            mode="multiple"
            maxTagCount="responsive"
            value={selectedSectors}
            options={sectorOptions}
            onChange={setSectors}
          />
        </div>

        <div style={{ flex: 1 }}>
          <p className="margin-bottom-tiny">{t("user.enums.permissionsGroup._label")}</p>
          <Form.Item name="permissionGroup" noStyle>
            <Select<PermissionGroup>
              style={{ width: "100%" }}
              {...selectStandardProps}
              allowClear
              options={permissionGroupOptions}
            />
          </Form.Item>
        </div>

        <ActionButton size="middle" variant="outlined" icon="menu-unfold" onClick={handlePermissionsSetClick}>
          {t("user.actions.setDefaultPermissions")}
        </ActionButton>
      </Flex>
    </Card>
  );
};
